/**
 * Shared utility for displaying entity type information (icons, labels, counts)
 * Used by ProjectList, ProjectBox, and other components that display work units
 */
import { entityTypeStyles, getEntityTypeKey } from '../constants/constants';

export interface EntityDisplayInfo {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    bgClass: string;
    iconClass: string;
    labelClass: string;
    sublabel: string;
    chipClass: string;
    type: string;
    aiEntityType: string;
}


export const getEntityTypeForAi = (entityType: string) => {
    if (entityType === 'epic_advanced') return 'Container Epic';
    if (entityType === 'epic_normal') return 'Execution Epic';
    
    return entityType;
}
/**
 * Get entity type display info (icon, colors, labels) based on entity_type and type (roadmap/project)
 */
export const getEntityTypeDisplay = (entityType?: string, type?: string): EntityDisplayInfo => {
    const key = getEntityTypeKey(entityType, type);
    const style = entityTypeStyles[key];
    const aiEntityType = getEntityTypeForAi(key);

    // Build sublabel for child items
    // Always use "Work Item" (singular) for universal consistency
    const sublabel = 'Work Item';

    return {
        ...style,
        type: key,
        sublabel,
        aiEntityType,
    };
};

/**
 * Get the count of child items based on item type
 */
export const getChildItemCount = (item: {
    type?: string;
    stats?: {
        total?: number;
        total_projects?: number;
    };
    child_entity_types?: Record<string, number> | null;
}): number => {
    // If child_entity_types exists, sum values
    if (item.child_entity_types) {
        return Object.values(item.child_entity_types).reduce((a, b) => a + b, 0);
    }
    
    // Fallback to stats
    if (item.type === 'roadmap') {
        return item.stats?.total_projects ?? 0;
    }
    return item.stats?.total ?? 0;
};

/**
 * Format pluralized entity type label
 */
export const pluralizeEntityType = (count: number, entityType: string | undefined): string => {
    if (!entityType) return '';
    
    const lower = entityType.toLowerCase();

    // Singular cases - Capitalize
    if (count === 1) {
        if (lower === 'user story') return 'User Story';
        if (lower === 'work item') return 'Work Item';
        // Default capitalize first letter for generic cases
        return entityType.charAt(0).toUpperCase() + entityType.slice(1);
    }

    // Plural cases - UI Consistency (Capitalized)
    if (lower === 'user story') return 'User Stories';
    if (lower === 'story') return 'Stories';
    if (lower === 'task') return 'Tasks';
    if (lower === 'feature') return 'Features';
    if (lower === 'work item') return 'Work Items';
    if (lower === 'epic') return 'Epics';
    if (lower === 'bug') return 'Bugs';
    if (lower === 'issue') return 'Issues';

    // Default: add 's' for most cases
    return entityType + 's';
};

/**
 * Helper to get breakdown of child entities for tooltip
 */
export const getChildEntityBreakdown = (childEntityTypes?: Record<string, number> | null): Array<{type: string, count: number, label: string}> => {
    if (!childEntityTypes) return [];
    return Object.entries(childEntityTypes).map(([type, count]) => ({
        type: type,
        count: count,
        label: pluralizeEntityType(count, type)
    }));
};

/**
 * Check if an item is an Advanced Epic (type is roadmap and entity_type is epic)
 * Advanced Epics are expandable in the data table
 */
export const isAdvancedEpic = (item: {
    entity_type?: string;
    type?: string;
    child_entities?: any[];
}): boolean => {
    const entityType = item.entity_type?.toLowerCase();
    const itemType = item.type?.toLowerCase();
    
    // Advanced Epic: entity_type is 'epic' and type is 'roadmap'
    return entityType === 'epic' && itemType === 'roadmap';
};

/**
 * Check if an item has expandable child entities
 */
export const hasExpandableChildren = (item: {
    entity_type?: string;
    type?: string;
    child_entities?: any[];
}): boolean => {
    return isAdvancedEpic(item) &&
        Array.isArray(item.child_entities) &&
        item.child_entities.length > 0;
};


export const getAiEntityType = (entity_type: string, api_type: string, type: string): string => {
    if (entity_type === 'epic' && api_type === 'roadmap') {
        return 'Container Epic';
    }
    if (entity_type === 'epic' && api_type === 'project') {
        return 'Execution Epic';
    }
    if (entity_type === 'feature') {
        return 'Feature';
    }
    if (entity_type === 'work_unit') {
        return 'Work Item';
    }

    return type;
}